import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { environment } from '../../environments/environment';

/** OAuth Client ID of the brafton.com "Internal" Google Cloud web client. */
export const GOOGLE_OAUTH_CLIENT_ID = new InjectionToken<string>('GOOGLE_OAUTH_CLIENT_ID', {
  providedIn: 'root',
  factory: () => environment.google.clientId,
});

/** Google Identity Services script (browser OAuth code client). */
const IDENTITY_SERVICES_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

/** Lets the API read Google Docs the user can open (the audit's copy docs / DRD). */
export const GOOGLE_DOCUMENTS_READ_SCOPE = 'https://www.googleapis.com/auth/documents.readonly';

/** Lets the API read Google Sheets the user can open (copy docs / DRDs kept as sheets). */
export const GOOGLE_SPREADSHEETS_READ_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly';

/**
 * What the API needs to act in the user's Drive: their identity (to match the
 * signed-in user), per-file Drive access (only files the app creates), and
 * read-only Google Docs and Sheets access so the feedback audit can read the copy
 * docs and DRDs added as project assets or pasted in as links.
 */
const GOOGLE_EXPORT_SCOPES = `openid email https://www.googleapis.com/auth/drive.file ${GOOGLE_DOCUMENTS_READ_SCOPE} ${GOOGLE_SPREADSHEETS_READ_SCOPE}`;

/** Why the consent popup produced no code, so callers can show a fitting message. */
export type GoogleAuthCodeFailure = 'popup_blocked' | 'cancelled' | 'denied' | 'failed';

export class GoogleAuthCodeError extends Error {
  constructor(
    readonly reason: GoogleAuthCodeFailure,
    message: string,
  ) {
    super(message);
    this.name = 'GoogleAuthCodeError';
  }
}

/*
 * Minimal typings for the parts of Google Identity Services used here (the script
 * is loaded at runtime, so there is no package to import types from).
 */
interface GoogleCodeResponse {
  code?: string;
  error?: string;
  error_description?: string;
}

interface GoogleCodeClientError {
  type: 'popup_failed_to_open' | 'popup_closed' | 'unknown';
}

interface GoogleCodeClientConfig {
  client_id: string;
  scope: string;
  ux_mode: 'popup';
  login_hint?: string;
  hd?: string;
  callback: (codeResponse: GoogleCodeResponse) => void;
  error_callback?: (codeClientError: GoogleCodeClientError) => void;
}

interface GoogleIdentityServices {
  accounts: {
    oauth2: {
      initCodeClient(config: GoogleCodeClientConfig): { requestCode(): void };
    };
  };
}

type WindowWithGoogle = Window & { google?: GoogleIdentityServices };

/**
 * Runs Google's "Connect Google" consent popup and returns a one-time authorization
 * code. The code goes to the API (GoogleConnectionService.connect), which exchanges
 * it with the client secret and keeps the refresh token server-side — the browser
 * never holds a Google token.
 *
 * The popup must open inside a user gesture or browsers block it, so call
 * `preload()` ahead of time (e.g. when the export dialog opens), then call
 * `requestCode()` synchronously from the click handler, before any `await`.
 */
@Injectable({ providedIn: 'root' })
export class GoogleAuthCodeService {
  private readonly document = inject(DOCUMENT);
  private readonly clientId = inject(GOOGLE_OAUTH_CLIENT_ID);

  private identityServicesLoad: Promise<GoogleIdentityServices> | null = null;

  /** Starts loading the Google Identity Services script, ignoring failures. */
  preload(): void {
    this.loadIdentityServices().catch(() => undefined);
  }

  /**
   * Opens the consent popup for `email`'s brafton.com Google account and resolves
   * the authorization code. Call synchronously from a click handler.
   */
  requestCode(email: string): Promise<string> {
    const loadedIdentityServices = this.loadedIdentityServices();
    if (loadedIdentityServices) {
      // Already loaded: open synchronously so the popup keeps the user gesture.
      return this.openConsentPopup(loadedIdentityServices, email);
    }
    // Not preloaded: the popup may be blocked once the script finishes loading.
    return this.loadIdentityServices().then((identityServices) =>
      this.openConsentPopup(identityServices, email),
    );
  }

  private loadedIdentityServices(): GoogleIdentityServices | null {
    const identityServices = (this.document.defaultView as WindowWithGoogle | null)?.google;
    return identityServices?.accounts?.oauth2 ? identityServices : null;
  }

  private loadIdentityServices(): Promise<GoogleIdentityServices> {
    const loadedIdentityServices = this.loadedIdentityServices();
    if (loadedIdentityServices) {
      return Promise.resolve(loadedIdentityServices);
    }
    if (!this.identityServicesLoad) {
      this.identityServicesLoad = new Promise<GoogleIdentityServices>((resolve, reject) => {
        const scriptElement = this.document.createElement('script');
        scriptElement.src = IDENTITY_SERVICES_SCRIPT_URL;
        scriptElement.async = true;
        scriptElement.onload = () => {
          const identityServices = this.loadedIdentityServices();
          if (identityServices) {
            resolve(identityServices);
          } else {
            reject(new GoogleAuthCodeError('failed', 'Google sign-in did not initialize.'));
          }
        };
        scriptElement.onerror = () => {
          // Allow a later attempt to retry the load.
          this.identityServicesLoad = null;
          scriptElement.remove();
          reject(new GoogleAuthCodeError('failed', 'Could not load Google sign-in.'));
        };
        this.document.head.appendChild(scriptElement);
      });
    }
    return this.identityServicesLoad;
  }

  private openConsentPopup(identityServices: GoogleIdentityServices, email: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const codeClient = identityServices.accounts.oauth2.initCodeClient({
        client_id: this.clientId,
        scope: GOOGLE_EXPORT_SCOPES,
        ux_mode: 'popup',
        // Pre-selects the signed-in user's account and limits the chooser to Brafton.
        login_hint: email,
        hd: 'brafton.com',
        callback: (codeResponse) => {
          if (codeResponse.error || !codeResponse.code) {
            reject(
              new GoogleAuthCodeError(
                'denied',
                codeResponse.error_description || 'Google access was not granted.',
              ),
            );
            return;
          }
          resolve(codeResponse.code);
        },
        error_callback: (codeClientError) => {
          if (codeClientError.type === 'popup_failed_to_open') {
            reject(
              new GoogleAuthCodeError(
                'popup_blocked',
                'The Google sign-in popup was blocked. Allow popups for this site and try again.',
              ),
            );
          } else if (codeClientError.type === 'popup_closed') {
            reject(new GoogleAuthCodeError('cancelled', 'Google sign-in was cancelled.'));
          } else {
            reject(new GoogleAuthCodeError('failed', 'Google sign-in failed.'));
          }
        },
      });
      codeClient.requestCode();
    });
  }
}
